import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "./AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { environment, googleOAuthKey, guestbookSites } from "../../settings/IntegrationConstants";
import { BookingEngine, Rates } from "../../hotel_integration_modules/index";
import spaUrlChanged from "../../hotel_integration_modules/utilities/spaUrlChanged";
import onContentChange from "../../hotel_integration_modules/utilities/onContentChange";
import onContentChangeArray from "../../hotel_integration_modules/utilities/onContentChangeArray";
import UseWindowWidth from "../../hooks/UseWindowWidth";
import I18n from "../../translations/i18n";
import Widget from "./Widget";
import LoginModal from "./LoginModal";
import RatesMessaging from "./RatesMessaging";
import EnrollmentMessage from "./EnrollmentMessage";
import ConfirmationMessage from "./ConfirmationMessage";
import Theme from "./Theme";
import HoverOverMessage from "./HoverOverMessage";
import { isTablet } from "react-device-detect";

import "./HotelIntegration.css";
import rudderAnalytics from "../../utilities/rudderanalytics";
import axios from "axios";
import TotalBanner from "./TotalBanner";

type WidgetProps = {
  bookingEngine: BookingEngine;
  hotelSettings: any;
};

const HotelIntegration = ({ bookingEngine, hotelSettings }: WidgetProps) => {
  // Grab authContext to check if user is logged in and if so grab their email, first and last name and member code
  const authContext: any = useContext(AuthContext);
  const loggedIn = authContext?.logged_in ? true : false;
  // Have integation set to show specific components depending on whether the page value is set to true
  const [showWidget, setShowWidget] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalSectionType, setModalSectionType] = useState("login");
  const [showRatesPageMessaging, setShowRatesPageMessaging] = useState(false);
  const [showPaymentPageMessaging, setShowPaymentPageMessaging] = useState(false);
  const [
    showConfirmationPageMessage,
    setShowConfirmationPageMessage,
  ] = useState(false);
  const [showMessaging, setShowMessaging] = useState(true);
  const [showPartialMessaging, setShowPartialMessaging] = useState(true);
  const [messagingCoordinates, setMessagingCoordinates] = useState<
    null | [number, number]
  >(null);
  const [messagingHeight, setMessagingHeight] = useState(0);
  const [showHoverOver, setShowHoverOver] = useState(false);
  const [rates, setRates] = useState<Rates[]>([]);
  const [ratesCurrency, setRatesCurrency] = useState("");
  const [checkoutCurrency, setCheckoutCurrency] = useState("");
  const [confirmationCurrency, setConfirmationCurrency] = useState("");
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [existingMember, setExistingMember] = useState(false);
  const [gbEnrollChecked, setGbEnrollChecked] = useState(false);
  const [enrollmentTarget, setEnrollmentTarget] = useState(bookingEngine.getEnrollmentTarget());
  const totalBannerTarget = bookingEngine.getTotalBannerTarget?.();
  const confirmationTarget = bookingEngine.getConfirmationTarget();
  const widgetTarget = bookingEngine.getWidgetTarget();
  const widgetMobileAtWidth =
    bookingEngine.widgetMobileAtWidth !== undefined
      ? bookingEngine.widgetMobileAtWidth
      : 769;
  const saveInfoAtConfirmation =
    bookingEngine.saveInfoAtConfirmation !== undefined
      ? bookingEngine.saveInfoAtConfirmation
      : false;
  const autoExpandOnLoad =
    bookingEngine.autoExpandOnLoad !== undefined ? bookingEngine.autoExpandOnLoad :
      hotelSettings.widget.expandEnabled !== undefined ? hotelSettings.widget.expandEnabled :
        true;
  const WindowWidth = UseWindowWidth();
  const isDesktopWidget = WindowWidth > widgetMobileAtWidth - 1;
  const [outerAccordionExpanded, setOuterAccordionExpanded] = useState(
    sessionStorage.getItem("onLoadModalExpanded") === "true" &&
    sessionStorage.getItem("gbWidgetExpanded") === "true" &&
    !gbEnrollChecked &&
    !existingMember &&
    isDesktopWidget
  );
  const [isTgbEssentialsActive, setIsTgbEssentialsActive] = useState<boolean>(localStorage.getItem('TGBEssentials') === 'true');
  const isTgbEssentialsProperty = hotelSettings.widget.isTgbEssentials === 'true';
  const beiDocument = bookingEngine.shadowRoot || document;
  const [showTotalBanner, setShowTotalBanner] = useState(false)
  const [hoverOverSide, setHoverOverSide] = useState<'left' | 'right'>('left');

  const print = (s) => {
    const debug = process.env.NODE_ENV == 'development' || process.env.NODE_ENV == 'staging';
    if (debug) console.log('%c' + s, 'background: #fffcf2; color: #350091');
  }

  useEffect(() => {
    if (!isTgbEssentialsProperty || isTgbEssentialsActive) { return }

    const isFromGuestbook =
      document.referrer.includes('theguestbook') ||
      sessionStorage.getItem('tgb_app') === 'true' ||
      !!document.querySelector('#gopher-installed');

    if (isFromGuestbook) {
      localStorage.setItem('TGBEssentials', 'true');
      setIsTgbEssentialsActive(true);
    } else {
      attachEmailListener();
    }
  }, []);

  useEffect(() => {
    if (isTgbEssentialsProperty && !isTgbEssentialsActive) { return }

    sessionStorage.setItem("onLoadModalExpanded", "false");
    addAllListeners();
    // Should BEI be SPA, we'll need to clear all listener and set them again
    if (bookingEngine.singlePageApp) {
      spaUrlChanged(() => {
        if (bookingEngine.clearAllListeners) bookingEngine.clearAllListeners();
        setShowMessaging(bookingEngine.shouldDisplayMessaging());
        if (bookingEngine.shouldDisplayPartialMessaging)
          setShowPartialMessaging(
            bookingEngine.shouldDisplayPartialMessaging()
          );
        setShowWidget(false);
        setShowRatesPageMessaging(false);
        setShowPaymentPageMessaging(false);
        setShowConfirmationPageMessage(false);
        addAllListeners();
      });
    }
    setShowMessaging(bookingEngine.shouldDisplayMessaging());
    if (bookingEngine.shouldDisplayPartialMessaging)
      setShowPartialMessaging(bookingEngine.shouldDisplayPartialMessaging());
  }, [isTgbEssentialsActive]);

  useEffect(() => {
    if (isTgbEssentialsProperty && !isTgbEssentialsActive) { return }

    setUserLoggedIn(loggedIn);
    if (loggedIn) {
      bookingEngine.getMemberCode();
      bookingEngine.prefillUserInfo(
        authContext.first_name,
        authContext.last_name,
        authContext.email
      );

      sessionStorage.setItem("gbEmail", authContext.email);
      sessionStorage.setItem("gbFirstName", authContext.first_name);
      sessionStorage.setItem("gbLastName", authContext.last_name);
    } else {
      sessionStorage.setItem("gbEmail", "");
      sessionStorage.setItem("gbFirstName", "");
      sessionStorage.setItem("gbLastName", "");
    }
  }, [loggedIn, isTgbEssentialsActive]);

  const checkIfGBMember = async (email: string) => {
    try {
      const response = await axios.get(
        `${guestbookSites[environment as string].rewardsdash.url
        }/api/v1/bei/member_info`,
        {
          params: {
            email: email,
          },
        }
      )
      return response.status === 200 && response.data.is_current_member
    } catch (error) {
      console.log(`Cannot fetch memeber info: ${error}`)
    }
  }

  const attachEmailListener = () => {
    bookingEngine.addPaymentPageListener(async () => {
      const emailField = beiDocument.querySelector(
        bookingEngine.selectors.paymentPage.email
      );

      emailField?.addEventListener("blur", async () => {
        const isGBMember = await checkIfGBMember(emailField.value.trim());
        if (isGBMember) {
          localStorage.setItem('TGBEssentials', 'true');
          setIsTgbEssentialsActive(true);
        }
      });
    });
  }

  const addAllListeners = () => {
    if (sessionStorage.getItem('tgb_app')) {
      rudderAnalytics.track('source_from_app',
        {
          email: sessionStorage.getItem('user_email'),
        }
      );
    }

    bookingEngine.addWidgetListener(() => {
      widgetHandler();
    });
    bookingEngine.addRatesPageListener(() => {
      ratesPageHandler();
      // Some BEs might require ratesPagehandler to be called again on resize
      if (bookingEngine.requireResizeListener) {
        window.addEventListener("resize", () => ratesPageHandler());
      }
      onContentChangeArray(
        bookingEngine.selectors.ratesPage.contentChangeTarget,
        () => {
          print("RATES CHANGE DETECTED");
          setShowMessaging(bookingEngine.shouldDisplayMessaging());
          if (bookingEngine.shouldDisplayPartialMessaging)
            setShowPartialMessaging(
              bookingEngine.shouldDisplayPartialMessaging()
            );
          ratesPageHandler();

          if (bookingEngine.name === "selfbook")
            widgetHandler();
        },
        beiDocument,
      );
    });
    bookingEngine.addPaymentPageListener(() => {
      paymentPageHandler();
      onContentChange(
        bookingEngine.selectors.paymentPage.contentChangeTarget,
        () => {
          print("PAYMENT CHANGE DETECTED");
          paymentPageHandler();
        },
        beiDocument
      );
    });
    bookingEngine.addConfirmationPageListener(() => {
      confirmationPageHandler();
      onContentChange(
        bookingEngine.selectors.confirmationPage.contentChangeTarget,
        () => {
          print('CONFIRMATION CHANGE DETECTED');
          confirmationPageHandler();
        },
        beiDocument
      );
    });
    if (bookingEngine.addCancellationPageListener) {
      bookingEngine.addCancellationPageListener();
    }
  };

  const widgetHandler = () => {
    print("widgetHandler");

    // dont show widget on confirmation page
    // TODO: IF NOTHING BREAK BY 9/30 start removing #guestbook-widget display:none css on bookingEngines
    if (document.querySelector(bookingEngine.selectors.confirmationPage.waitFor)) {
      setShowWidget(false);
      return;
    }

    setShowWidget(true);
    setShowMessaging(bookingEngine.shouldDisplayMessaging());
    if (bookingEngine.additionalWidgetFunctions) {
      bookingEngine.additionalWidgetFunctions();
    }
  };

  const ratesPageHandler = () => {
    print("ratesPageHandler");
    if (bookingEngine.additionalRatesPageFunctions) {
      bookingEngine.additionalRatesPageFunctions();
    }
    bookingEngine.setLocale();
    let ratesObject = bookingEngine.getRates();
    setRates(ratesObject);
    const currency = bookingEngine.getRatesCurrency();
    setRatesCurrency(currency);
    setShowRatesPageMessaging(true);

    if (bookingEngine.name === 'mews' &&
      beiDocument.querySelector(bookingEngine.selectors.paymentPage.contentChangeTarget)) {
      setShowPaymentPageMessaging(false);
    }

    if (window.innerWidth <= 768 && bookingEngine.name === 'skipper') {
      bookingEngine.shadowRoot.querySelector(bookingEngine.selectors.ratesPage.selectRoomPageScroll)?.addEventListener('scroll', () => {
        setShowHoverOver(false);
      });
      bookingEngine.shadowRoot.querySelector(bookingEngine.selectors.ratesPage.selectOfferPageScroll)?.addEventListener('scroll', () => {
        setShowHoverOver(false);
      });
    }
  };

  const paymentPageHandler = () => {
    print("paymentPageHandler");
    setShowMessaging(bookingEngine.shouldDisplayMessaging());
    if (bookingEngine.additionalPaymentsPageFunctions) {
      bookingEngine.additionalPaymentsPageFunctions();
    }

    // hotelkey (mobile) has an interval checker for subtotal element
    // need async messaging to get correct rewards
    if ((bookingEngine.name === 'hotelkey' && window.innerWidth < 767)
      || bookingEngine.name === 'simplebooking'
      || bookingEngine.name === 'guest_centric'
      || bookingEngine.name === 'pegs'
      || bookingEngine.name === 'mirai'
    ) {
      setTimeout(() => {
        bookingEngine.createEnrollmentMessageContainer();
        setShowPaymentPageMessaging(true);
        setEnrollmentTarget(bookingEngine.getEnrollmentTarget());
      }, 1000);
    } else {
      bookingEngine.createEnrollmentMessageContainer();
      setEnrollmentTarget(bookingEngine.getEnrollmentTarget());
      setShowPaymentPageMessaging(true);
    }

    if (bookingEngine.useTotalBanner) {
      setShowTotalBanner(true)
    }

    bookingEngine.setLocale();
    const currency = bookingEngine.getCheckoutCurrency();
    setCheckoutCurrency(currency);
    if (!saveInfoAtConfirmation || bookingEngine.name === "synxis_gtm" || bookingEngine.name === "revraise")
      bookingEngine.savePaymentPageInfo();
  };

  const confirmationPageHandler = () => {
    print("confirmationPageHandler");
    if (bookingEngine.additionalConfirmationPageFunctions) {
      bookingEngine.additionalConfirmationPageFunctions();
    }
    if (saveInfoAtConfirmation) {
      console.log("Saving Booking Information...");
      bookingEngine.savePaymentPageInfo();
    }
    if (sessionStorage.getItem("gbBookingDetails")) {
      bookingEngine.createConfirmationMessageContainer();
      bookingEngine.setLocale();

      const currency = () => {
        if (bookingEngine.name === "travelclick") {
          return bookingEngine.getCheckoutCurrency();
        }
        else if (checkoutCurrency) {
          return checkoutCurrency;
        }
        else if (bookingEngine.getCheckoutCurrency()) {
          return bookingEngine.getCheckoutCurrency();
        }
        else {
          return "$";
        }
      }
      setConfirmationCurrency(currency);
      setShowConfirmationPageMessage(true);
      console.log("Confirmation Message Created!");
    } else {
      console.log("No gbBookingDetails found");
    }
  };

  const handleLoginModalOpen = (modalSection: string) => {
    if (modalSection === "login") {
      setModalSectionType("login");
    } else {
      setModalSectionType("signup");
    }
    setShowLoginModal(true);
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  const integrationConfigs = {
    primary_color: hotelSettings.widget.primaryColor,
    secondary_color: hotelSettings.widget.secondaryColor,
    white_label: hotelSettings.hotel.whiteLabel,
    primary_font: bookingEngine.hotel.inheritBeFont ? "inherit" : "Roboto",
    secondary_font: bookingEngine.hotel.inheritBeFont ? "inherit" : "Work Sans",
    btn_color: "292C2C",
    btn_hover_color: "555F66",
    btn_pressed_color: "111415",
  };

  const locale = I18n.translations[window.gb_data.I18n.locale]
    ? window.gb_data.I18n.locale
    : "en";

  if (isTgbEssentialsProperty && !isTgbEssentialsActive) { return null }

  return (
    <Theme integrationConfigs={integrationConfigs}>
      <HoverOverMessage
        locale={locale}
        show={showHoverOver}
        coordinates={messagingCoordinates}
        messagingHeight={messagingHeight}
        handleOpen={handleLoginModalOpen}
        hotelSettings={hotelSettings}
        mobileTooltip={!!bookingEngine.mobileTooltip}
        hoverOverSide={hoverOverSide}
        setShowHoverOver={setShowHoverOver}
      />

      {showRatesPageMessaging &&
        rates.map((rate, index) => (
          <RatesMessaging
            showMessaging={showMessaging}
            key={index}
            bookingEngine={bookingEngine}
            rate={rate}
            currency={ratesCurrency}
            locale={locale}
            setOuterAccordionExpanded={setOuterAccordionExpanded}
            widgetMobileAtWidth={widgetMobileAtWidth}
            showHoverOver={showHoverOver}
            setShowHoverOver={setShowHoverOver}
            setMessagingHeight={setMessagingHeight}
            setMessagingCoordinates={setMessagingCoordinates}
            setHoverOverSide={setHoverOverSide}
          />
        ))}
      {enrollmentTarget && showPaymentPageMessaging && showPartialMessaging && (
        <EnrollmentMessage
          showMessaging={showMessaging}
          bookingEngine={bookingEngine}
          existingMember={existingMember}
          gbEnrollChecked={gbEnrollChecked}
          currency={checkoutCurrency}
          locale={locale}
          setExistingMember={setExistingMember}
          setGbEnrollChecked={setGbEnrollChecked}
          windowWidth={WindowWidth}
          hotelSettings={hotelSettings}
        />
      )}
      {showTotalBanner && showPaymentPageMessaging && (
        <TotalBanner
          bookingEngine={bookingEngine}
          totalBannerTarget={totalBannerTarget}
          setShowHoverOver={setShowHoverOver}
          currency={checkoutCurrency}
          setMessagingHeight={setMessagingHeight}
          setHoverOverSide={setHoverOverSide}
          setMessagingCoordinates={setMessagingCoordinates}
          messageActive={gbEnrollChecked || existingMember}
          showMessaging={showMessaging}
          showHoverOver={showHoverOver}
        />
      )}
      {confirmationTarget && showConfirmationPageMessage && (
        <ConfirmationMessage
          bookingEngine={bookingEngine}
          currency={confirmationCurrency}
          locale={locale}
        />
      )}
      <GoogleOAuthProvider clientId={googleOAuthKey}>
        <LoginModal
          modalSectionType={modalSectionType}
          setModalSectionType={setModalSectionType}
          showLoginModal={showLoginModal}
          handleClose={handleLoginModalClose}
          locale={locale}
        />
      </GoogleOAuthProvider>
      {widgetTarget && showWidget && showMessaging && showPartialMessaging && (
        <Widget
          handleOpen={handleLoginModalOpen}
          existingMember={existingMember}
          gbEnrollChecked={gbEnrollChecked}
          userLoggedIn={userLoggedIn}
          bookingEngine={bookingEngine}
          hotelSettings={hotelSettings}
          widgetMobileAtWidth={widgetMobileAtWidth}
          locale={locale}
          autoExpandOnLoad={autoExpandOnLoad}
          setOuterAccordionExpanded={setOuterAccordionExpanded}
          outerAccordionExpanded={outerAccordionExpanded}
        />
      )}
    </Theme>
  );
};

export default HotelIntegration;
